import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { authEvents } from '@/api/auth-events';
import { ApiError, unwrap } from '@/api/envelope';
import {
  createPage,
  portalMe,
  previewPage,
  savePage,
  storePageImage,
  transitionPage,
  type PageCreate,
  type PageSave,
} from '@/api/generated';
import { arcRoot } from '@/lib/queryRoots';

import type { MoveTarget } from './pageActions';
import { toPageDetail } from './pageLabels';
import { useInvalidatePages, usePagesOrganizationId } from './usePages';

/** What a page image upload answers: the key to save, and an address to show it now. */
export interface StagedImage {
  key: string;
  /** Null when the reply gave none. */
  url: string | null;
}

export type ImagePurpose = 'header' | 'inline';

/**
 * What the image panel says for a server failure: the panel, not a toast,
 * because the person is looking at it and nothing else was written (P35).
 */
export const UPLOAD_FAILED = 'The image could not be uploaded. Please try again.';

/** A move's server failure, or a refusal the screen cannot name: a toast (P21). */
export const MOVE_FAILED = 'That change could not be made. Please try again.';

/** The statuses whose sentence is the backend's own about the file (B9). */
const FILE_REFUSALS = new Set([413, 415, 422]);

interface PageWritesOptions {
  /**
   * An upload's 403: the editor's alert (P21). Pass a stable function, as
   * the upload is made from it. The list, which uploads nothing, leaves it out.
   */
  onForbidden?: (error: ApiError) => void;
}

const IGNORE_FORBIDDEN = () => {};

/**
 * The editor's and the card menu's writes (design.md "Saving and
 * publishing"). Nothing here is toasted: each screen says the outcome where
 * design.md's refusal table puts it, and an upload's failures belong to the
 * panel that asked (P35).
 */
export function usePageWrites({
  onForbidden = IGNORE_FORBIDDEN,
}: PageWritesOptions = {}) {
  // Header and inline uploads together, so Save waits for every one (P19).
  const [uploading, setUploading] = useState(0);

  /**
   * After a 403, asks the server who the person is now, so a suspended
   * organization or a lost role withdraws the write controls (P21). The
   * answer goes the way a renewed profile does, through `AuthProvider`. A
   * failure to ask changes nothing.
   */
  const rereadWritability = useCallback(async () => {
    try {
      const reply = await portalMe({ throwOnError: true });
      authEvents.emitProfileUpdated(unwrap(reply.data));
    } catch {
      // The alert has said why; the controls stay as they were.
    }
  }, []);

  /**
   * Stores one image in the organization's page folder (B9, D12). A refusal
   * rejects with the sentence the panel shows; a 403 is handed to the editor,
   * writability reread, and rejects with no words, so the panel adds nothing.
   */
  const upload = useCallback(
    async (purpose: ImagePurpose, file: File): Promise<StagedImage> => {
      setUploading((count) => count + 1);
      try {
        const reply = await storePageImage({
          query: { purpose },
          body: { file },
          throwOnError: true,
        });
        const stored = unwrap(reply.data);
        return { key: stored.key, url: stored.url ?? null };
      } catch (cause) {
        if (cause instanceof ApiError && cause.status === 403) {
          onForbidden(cause);
          void rereadWritability();
          throw new Error('');
        }
        if (cause instanceof ApiError && FILE_REFUSALS.has(cause.status)) {
          throw new Error(cause.serverMessage ?? cause.message);
        }
        throw new Error(UPLOAD_FAILED);
      } finally {
        setUploading((count) => count - 1);
      }
    },
    [onForbidden, rereadWritability],
  );

  const invalidate = useInvalidatePages();
  const queryClient = useQueryClient();
  const organizationId = usePagesOrganizationId();
  /**
   * What the app shows has changed: the ARC is asked for again through its
   * root, never its own keys (P24, D19). Only after a write that succeeded.
   */
  const invalidateArc = useCallback(
    () => queryClient.invalidateQueries({ queryKey: arcRoot(organizationId) }),
    [queryClient, organizationId],
  );

  /**
   * A new page, in DRAFT at version 1 (B1). The pages are asked for again
   * whatever the answer: a refusal can mean the screen was out of date (I7).
   */
  const create = useMutation({
    mutationFn: async (body: PageCreate) => {
      const reply = await createPage({ body, throwOnError: true });
      return toPageDetail(unwrap(reply.data));
    },
    onSettled: invalidate,
  });

  /**
   * A save of a stored page, naming the version it was loaded from (B5, I6).
   * `live` says the page was published when the save was sent: that save is
   * live at once, so the ARC is asked for again; a draft's is not (D19).
   */
  const save = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: PageSave; live: boolean }) => {
      const reply = await savePage({ path: { page_id: id }, body, throwOnError: true });
      return toPageDetail(unwrap(reply.data));
    },
    onSuccess: (_page, { live }) => (live ? invalidateArc() : undefined),
    onSettled: invalidate,
  });

  /**
   * Publish, Unpublish and Remove (B6, D6). Every move that succeeds changes
   * what the ARC shows (P24); the pages are asked for again either way.
   */
  const transition = useMutation({
    mutationFn: async ({ id, to }: { id: string; to: MoveTarget }) => {
      const reply = await transitionPage({
        path: { page_id: id },
        body: { to },
        throwOnError: true,
      });
      return toPageDetail(unwrap(reply.data));
    },
    onSuccess: invalidateArc,
    onSettled: invalidate,
  });

  /** The unsaved page as the app would show it (B10, D16). It writes nothing. */
  const preview = useMutation({
    mutationFn: async (body: PageCreate) => {
      const reply = await previewPage({ body, throwOnError: true });
      return unwrap(reply.data);
    },
  });

  return {
    upload,
    uploading,
    create,
    save,
    transition,
    preview,
    rereadWritability,
    /**
     * Any page write in flight: the write buttons wait for it (P19, PNF7). A
     * preview writes nothing, so it is not one.
     */
    anyPending: create.isPending || save.isPending || transition.isPending,
  };
}
